import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { LoadingDots } from "PFComponents/loading_dots";
import NotFoundComponent from "PFComponents/not_found/not_found";
import useStorage from "PFCore/helpers/use_storage";
import { useActivity, useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { useInterestQuestionnaires } from "PFCore/hooks/queries/common/use_interest_questionnaires";
import { useNotificationsMarkAsRead } from "PFCore/hooks/queries/notifications/use_notifications_mark_as_read";
import { Activity, Id } from "PFTypes";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { TemplateKey } from "../../../constants/templates";
import { useTemplateFind } from "../../../hooks";
import { isActivityPending } from "../../helpers";
import { useActivityTemplateType } from "../../hooks";
import { ProfilesListMode } from "../../types/profiles_view_mode";
import { useMatchesShortlistTablesColumns } from "../hooks/use_matches_shortlist_tables_columns";
import ActivityShowContext from "./activity_show_context";

type LoadedActivityProps = React.PropsWithChildren & {
  activity: Activity;
};

const LoadedActivity = ({ activity, children }: LoadedActivityProps) => {
  const { t } = useTranslation("activities");

  const location = useLocation();
  const utmSource = location?.state?.utmSource;

  const { showGrowl } = useGrowlStoreActions();
  const findTemplate = useTemplateFind();

  const { availableProfilesListColumns, selectedProfilesListColumns, setSelectedProfilesListColumns } =
    useMatchesShortlistTablesColumns();
  const { isAudit, isEngagement, isRole } = useActivityTemplateType(activity);

  const { invalidate: invalidateActivities } = useActivityInvalidate();
  const { mutate: markNotificationsAsRead } = useNotificationsMarkAsRead();
  const hasOpenVacancy = activity.vacancies && activity.vacancies.total - activity.vacancies.closed > 0;

  const [profilesListMode, setProfilesListMode] = useStorage<ProfilesListMode>(
    "activity_profiles_view_mode",
    ProfilesListMode.Cards
  );
  const [refetchedAt, setRefetchedAt] = useState<null | string>(null);

  // Permissions
  const roleKey = isAudit ? TemplateKey.AuditRole : TemplateKey.Role;

  const template = findTemplate({ id: activity.template_id });
  const templateName = template ? t(`templates.${template.key}`) : "";
  const isFillDisabled = !hasOpenVacancy;

  useEffect(() => {
    if (["workflow", "email"].includes(utmSource || "")) {
      markNotificationsAsRead(isRole ? { roleId: activity.id } : { engagementId: activity.id });
    }
  }, []);

  useEffect(() => {
    isActivityPending(activity, true) &&
      showGrowl({
        message: (
          <div>
            <Trans
              i18nKey={"show.patientAsDetailsCurate"}
              t={t}
              components={[<></>]}
              values={{ templateName }}
            />
            <Button
              kind="text"
              text={t("show.clickToReload")}
              onClick={() =>
                invalidateActivities([activity.id]).then(() => setRefetchedAt(new Date().toISOString()))
              }
            />
          </div>
        ),
        kind: "alert",
        ttl: null
      });
  }, [refetchedAt]);

  return (
    <ActivityShowContext.Provider
      value={{
        activityId: activity.id,
        isAudit,
        isEngagement,
        isRole,
        roleKey,
        isFillDisabled,
        // Profiles list mode and columns
        profilesListMode,
        setProfilesListMode,
        availableProfilesListColumns,
        selectedProfilesListColumns,
        setSelectedProfilesListColumns
      }}
    >
      {children}
    </ActivityShowContext.Provider>
  );
};

type ActivityShowContextProviderProps = {
  id: Id;
  children: React.ReactNode;
};

const ActivityShowContextProvider = ({ id, children }: ActivityShowContextProviderProps) => {
  const { data: activity, isFetched: loaded, isError } = useActivity(id);
  const { isFetched: areInterestQuestionnairesFetched } = useInterestQuestionnaires();

  if (!loaded || !areInterestQuestionnairesFetched) {
    return <LoadingDots circlesEnabled pageViewEnabled />;
  }

  if (isError || !activity) {
    return <NotFoundComponent />;
  }

  return (
    <LoadedActivity key={activity.id} activity={activity}>
      {children}
    </LoadedActivity>
  );
};

export default ActivityShowContextProvider;
